import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { twJoin } from 'tailwind-merge';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { AdditionalInformationBox } from '@/ui/components/shared/additional-information-box/additional-information-box';
import { getOptionTileStateClasses } from '@/ui/components/shared/option-tile/option-tile-styles';
import { CartUpsellingItem } from './types/CartUpsellingItem';
import { CartUpsellingItemSection } from './types/CartUpsellingItemSection';

const CARD_BASE_CLASSES =
  'group mt-5 w-full rounded-xl p-4 shadow-nuki-blured-sm transition duration-200 ease-in-out';

interface CartAddOnSectionProps {
  section: CartUpsellingItemSection;
  selectedItems: CartUpsellingItem[];
  toggleSelection: (item: CartUpsellingItem) => void;
  setAndDisplayAdditionalInformation: (
    display: boolean,
    addOnSection?: CartUpsellingItemSection,
    boxId?: string,
  ) => void;
  activeBoxId?: string;
}

function CartAddOnSection({
  section,
  selectedItems,
  toggleSelection,
  setAndDisplayAdditionalInformation,
  activeBoxId,
}: Readonly<CartAddOnSectionProps>) {
  const { formatCurrency } = useCurrencyFormatter();
  const isSelected = selectedItems.some((item) => item.sku === section.item.sku);
  // The add-on cards take the chooser tiles' selection look, so a hovered card never reads as
  // selected and a selected one reads the same as in the chooser.
  const cardStateClasses = getOptionTileStateClasses(isSelected);

  return (
    <div>
      {section.description && (
        <div className="mt-5">
          <RichTextRenderer className=" text-lg" blocks={section.description} />
        </div>
      )}

      {section.designVariant === 'secondary' ? (
        <button
          type="button"
          className={twJoin(CARD_BASE_CLASSES, cardStateClasses)}
          aria-pressed={isSelected}
          onClick={() => toggleSelection(section.item)}
        >
          <div className="flex space-x-4">
            <div className="relative z-0 h-16 w-16 flex-shrink-0 rounded-sm border border-neutral-5">
              <div className="absolute bottom-0 left-0 right-0 -z-10 h-2/4 bg-gradient-to-t from-neutral-5 to-transparent opacity-30" />
              <OptimizedImage
                image={section.item.image}
                fill
                className="object-contain py-1"
                priority
                sizes="100px"
              />
            </div>
            <div className="w-full">
              <div className="flex justify-between gap-4 text-lg font-bold">
                <p>{section.item.name}</p>
                <p className="my-px">{formatCurrency(section.item.priceVariant)}</p>
              </div>
              <RichTextRenderer
                className="mt-1 text-left text-neutral-70"
                blocks={section.item.description}
              />
            </div>
          </div>
        </button>
      ) : (
        <button
          type="button"
          key={section.item.sku}
          className={twJoin(CARD_BASE_CLASSES, 'cursor-pointer', cardStateClasses)}
          aria-pressed={isSelected}
          onClick={() => toggleSelection(section.item)}
        >
          <div className="flex justify-between text-start font-bold ">
            <div>
              <div className="mb-2 text-md">{section.item.name}</div>
              <div>+{formatCurrency(section.item.priceVariant)}</div>
            </div>
            <div>
              {isSelected ? (
                <CheckCircleIcon className="h-6 w-6 text-neutral-100" />
              ) : (
                <CheckCircleIcon className="group h-6 w-6 text-neutral-10 transition duration-200 ease-in-out group-hover:text-primary-50" />
              )}
            </div>
          </div>
          <hr className="my-4 text-neutral-10" />
          <RichTextRenderer
            className="text-start text-sm text-neutral-50"
            blocks={section.item.description}
          />
        </button>
      )}

      <AdditionalInformationBox
        additionalInformation={section}
        activeBoxId={activeBoxId}
        onSelect={(boxId) => {
          setAndDisplayAdditionalInformation(true, section, boxId);
        }}
        onClear={() => {
          setAndDisplayAdditionalInformation(false, section);
        }}
      />
    </div>
  );
}

export default CartAddOnSection;
